import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sticky-sidebar"
export default class extends Controller {
  static targets = ["content"]
  
  connect() {
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    
    // Store original width before any positioning changes
    this.storeOriginalWidth()
    
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleResize)
    
    // Initial setup
    this.setupDimensions()
    this.handleScroll()
  }
  
  // Check if we're at medium breakpoint or above (768px)
  isDesktop() {
    return window.innerWidth >= 768
  }
  
  storeOriginalWidth() {
    // Get the computed width of the content target in its natural state
    this.originalWidth = this.contentTarget.getBoundingClientRect().width
  }
  
  disconnect() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  }
  
  setupDimensions() {
    // Get all the measurements we need
    this.sidebarRect = this.element.getBoundingClientRect()
    this.contentRect = this.contentTarget.getBoundingClientRect()
    this.mainContent = document.querySelector('[data-sidebar-target="content"]') || document.querySelector('main')
    
    // Store the original width when sidebar is in normal flow
    this.originalWidth = this.element.offsetWidth
    
    if (this.mainContent) {
      this.mainContentRect = this.mainContent.getBoundingClientRect()
    }
  }
  
  handleResize() {
    // Reset positioning to get natural dimensions
    this.resetPositioning()
    
    // Only continue with sticky setup if we're on desktop
    if (!this.isDesktop()) {
      return
    }
    
    // Recalculate dimensions on resize
    this.storeOriginalWidth()
    this.setupDimensions()
    this.handleScroll()
  }
  
  resetPositioning() {
    this.contentTarget.style.position = ''
    this.contentTarget.style.top = ''
    this.contentTarget.style.bottom = ''
    this.contentTarget.style.width = ''
  }
  
  handleScroll() {
    // Only run sticky behavior on desktop/tablet
    if (!this.isDesktop()) {
      this.resetPositioning()
      return
    }
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const viewportHeight = window.innerHeight
    
    // Get current positions
    const sidebarTop = this.element.offsetTop
    const sidebarHeight = this.contentTarget.offsetHeight
    const documentHeight = document.documentElement.scrollHeight
    
    // Calculate boundaries
    const topBoundary = sidebarTop
    const bottomBoundary = documentHeight - viewportHeight
    const sidebarBottomPosition = scrollTop + viewportHeight
    const contentBottom = sidebarTop + sidebarHeight
    
    // Reset any previous positioning
    this.resetPositioning()
    
    // Case 1: Sidebar is shorter than viewport - normal sticky behavior
    if (sidebarHeight <= viewportHeight) {
      if (scrollTop > topBoundary) {
        // Stick to top of viewport
        this.contentTarget.style.position = 'fixed'
        this.contentTarget.style.top = '1rem' // 16px gap from top
        this.contentTarget.style.width = `${this.originalWidth}px`
        
        // Check if we're near the bottom of the page
        if (sidebarBottomPosition + sidebarHeight > documentHeight - 32) {
          // Stick to bottom instead
          this.contentTarget.style.top = ''
          this.contentTarget.style.bottom = '1rem'
        }
      }
    } 
    // Case 2: Sidebar is taller than viewport - smart sticky behavior
    else {
      const sidebarVisibleTop = Math.max(0, scrollTop - topBoundary)
      const sidebarVisibleBottom = sidebarVisibleTop + viewportHeight
      
      // If scrolling down and sidebar bottom would go out of view
      if (scrollTop > topBoundary && sidebarVisibleBottom >= sidebarHeight) {
        // Stick bottom of sidebar to bottom of viewport
        this.contentTarget.style.position = 'fixed'
        this.contentTarget.style.bottom = '1rem'
        this.contentTarget.style.width = `${this.originalWidth}px`
        
        // But don't let it go past the document bottom
        if (sidebarBottomPosition > documentHeight - 32) {
          this.contentTarget.style.bottom = `${sidebarBottomPosition - documentHeight + 32}px`
        }
      }
      // If scrolling up and sidebar top would go out of view
      else if (scrollTop > topBoundary && sidebarVisibleTop <= 0) {
        // Stick top of sidebar to top of viewport
        this.contentTarget.style.position = 'fixed'
        this.contentTarget.style.top = '1rem'
        this.contentTarget.style.width = `${this.originalWidth}px`
      }
      // If we're in the middle and sidebar fits in viewport
      else if (scrollTop > topBoundary) {
        this.contentTarget.style.position = 'fixed'
        this.contentTarget.style.top = '1rem'
        this.contentTarget.style.width = `${this.originalWidth}px`
      }
    }
  }
}